import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMousePointer,
  faRuler,
  faRotateLeft,
  faRotateRight,
  faExchangeAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useDesignContext } from "../../contexts/design.context";
import { useWhiteLabelContext } from "../../../../../WhiteLabelContext";
import { DesignEditorToolType } from "../../models/enums/design-editor-tool-type.enum";
import { Direction } from "../../../design-editor/design-editor-tool/DesignEditorTool.vm";
import React, { useState, useEffect } from "react";
import { Menu, MenuItem } from "@mui/material";

export const DesignEditorToolbar = () => {
  const {
    activeTool,
    setActiveTool,
    measurementColor,
    setMeasurementColor,
    selectedLogo,
    rotateLogo,
    moveLogoToView,
    views,
    selectedView,
  } = useDesignContext();
  const { whiteLabel } = useWhiteLabelContext();
  const [showColorDrawer, setShowColorDrawer] = useState(false);
  const [moveMenuAnchorEl, setMoveMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  useEffect(() => {
    if (activeTool !== DesignEditorToolType.Ruler) {
      setShowColorDrawer(false);
    }
  }, [activeTool]);

  const handleMoveMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoveMenuAnchorEl(event.currentTarget);
  };

  const handleMoveMenuClose = () => {
    setMoveMenuAnchorEl(null);
  };

  const tools = [
    {
      type: DesignEditorToolType.Cursor,
      icon: faMousePointer,
      label: "Cursor Tool",
      description: "Select, move, and rotate logos",
      onClick: () => {
        setActiveTool(DesignEditorToolType.Cursor);
        setShowColorDrawer(false);
      },
    },
    {
      type: DesignEditorToolType.Ruler,
      icon: faRuler,
      label: "Ruler Tool",
      description: "Measure distances on the design",
      onClick: () => {
        if (activeTool === DesignEditorToolType.Ruler) {
          // Toggle drawer if already active
          setShowColorDrawer(!showColorDrawer);
        } else {
          setActiveTool(DesignEditorToolType.Ruler);
          setShowColorDrawer(true);
        }
      },
    },
  ];

  const colors = [
    { hex: "#ff0000", label: "Red" }, // Red
    { hex: "#0389f7", label: "Blue" }, // Blue
    { hex: "#37b521", label: "Green" }, // Green
  ];

  return (
    <div className="absolute right-4 top-4 flex flex-row items-start gap-2 z-20">
      {/* Main Toolbar */}
      <div className="flex flex-col gap-2 bg-white p-2 rounded-lg border border-borderGray shadow-sm">
        {tools.map((tool) => (
          <div
            key={tool.type}
            className="relative flex items-center justify-center"
          >
            <Tooltip
              title={
                <div className="text-center">
                  <div className="font-bold text-xs">{tool.label}</div>
                  <div className="text-xs">{tool.description}</div>
                </div>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  className: "bg-gray-200",
                },
                arrow: {
                  className: "text-gray-200",
                },
              }}
            >
              <button
                onClick={tool.onClick}
                className={`p-2 rounded hover:bg-gray-100 flex items-center justify-center w-10 h-10 transition-colors ${
                  activeTool === tool.type ? "" : "text-gray-600"
                }`}
                style={{
                  color:
                    activeTool === tool.type ? whiteLabel.colorHex : undefined,
                  backgroundColor:
                    activeTool === tool.type
                      ? `${whiteLabel.colorHex}20`
                      : undefined,
                }}
              >
                <FontAwesomeIcon icon={tool.icon} size="xl" />
              </button>
            </Tooltip>

            {/* Rotation Drawer (Cursor Tool) */}
            {tool.type === DesignEditorToolType.Cursor &&
              activeTool === DesignEditorToolType.Cursor &&
              selectedLogo && (
                <div className="absolute right-full mr-3 flex flex-row gap-2 bg-white p-2 rounded-lg border border-borderGray shadow-sm animate-drawerReveal origin-right -z-10">
                  <Tooltip
                    title={<div className="font-bold text-xs">Rotate Left</div>}
                    placement="bottom"
                    arrow
                    componentsProps={{
                      tooltip: {
                        className: "bg-gray-200 text-gray-800",
                      },
                      arrow: {
                        className: "text-gray-200",
                      },
                    }}
                  >
                    <button
                      onClick={() => rotateLogo(selectedLogo, Direction.Left)}
                      className="w-8 h-8 rounded border border-transparent hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-all hover:scale-105"
                    >
                      <FontAwesomeIcon icon={faRotateLeft} size="lg" />
                    </button>
                  </Tooltip>
                  <Tooltip
                    title={
                      <div className="font-bold text-xs">Rotate Right</div>
                    }
                    placement="bottom"
                    arrow
                    componentsProps={{
                      tooltip: {
                        className: "bg-gray-200 text-gray-800",
                      },
                      arrow: {
                        className: "text-gray-200",
                      },
                    }}
                  >
                    <button
                      onClick={() => rotateLogo(selectedLogo, Direction.Right)}
                      className="w-8 h-8 rounded border border-transparent hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-all hover:scale-105"
                    >
                      <FontAwesomeIcon icon={faRotateRight} size="lg" />
                    </button>
                  </Tooltip>
                  <Tooltip
                    title={<div className="font-bold text-xs">Move logo</div>}
                    placement="bottom"
                    arrow
                    componentsProps={{
                      tooltip: {
                        className: "bg-gray-200 text-gray-800",
                      },
                      arrow: {
                        className: "text-gray-200",
                      },
                    }}
                  >
                    <button
                      onClick={handleMoveMenuOpen}
                      className="w-8 h-8 rounded border border-transparent hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-all hover:scale-105"
                    >
                      <FontAwesomeIcon icon={faExchangeAlt} size="lg" />
                    </button>
                  </Tooltip>
                  <Menu
                    anchorEl={moveMenuAnchorEl}
                    open={Boolean(moveMenuAnchorEl)}
                    onClose={handleMoveMenuClose}
                  >
                    {views
                      .filter((v) => v.id !== selectedView?.id)
                      .map((view) => (
                        <MenuItem
                          key={view.id}
                          onClick={() => {
                            moveLogoToView(selectedLogo, view);
                            handleMoveMenuClose();
                          }}
                        >
                          {view.view}
                        </MenuItem>
                      ))}
                  </Menu>
                </div>
              )}

            {/* Color Drawer (Ruler Tool) */}
            {tool.type === DesignEditorToolType.Ruler &&
              activeTool === DesignEditorToolType.Ruler &&
              showColorDrawer && (
                <div className="absolute right-full mr-3 flex flex-row gap-2 bg-white p-2 rounded-lg border border-borderGray shadow-sm animate-drawerReveal origin-right -z-10">
                  {colors.map((color) => (
                    <button
                      key={color.hex}
                      onClick={() => setMeasurementColor(color.hex)}
                      className={`w-8 h-8 rounded transition-all ${
                        measurementColor === color.hex
                          ? "scale-110"
                          : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.label}
                    />
                  ))}
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
};
